/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("WindowWidget", ["WidgetGroupBase", "WidgetFactory"], function (context, cls) {
  /**
   * Base class for widgets.
   * @class WindowWidget
   * @memberOf classes
   * @extends classes.WidgetGroupBase
   * @publicdoc
   */
  cls.WindowWidget = context.oo.Class(cls.WidgetGroupBase, function ($super) {
    return /** @lends classes.WindowWidget.prototype */ {
      __name: "WindowWidget",
      /**
       * @type {classes.ModalWidget}
       */
      _modalWidget: null,
      /**
       * title
       * @type {?string}
       */
      _text: null,
      /**
       * window icon
       * @type {?string}
       */
      _image: null,
      _topMenus: null,
      /** @type {classes.TopMenuWidget} */
      _activeTopMenuWidget: null,
      /** @type {classes.MenuWidget} */
      _menuWidget: null,
      /**
       * List of toolbar widgets
       * @type {Array}
       */
      _toolBarWidgets: null,
      _closeHostMenuOnClickHandler: null,
      _menuContainerTop: null,
      _menuContainerBottom: null,
      _menuContainerLeft: null,
      _menuContainerRight: null,
      _menuContainerMiddle: null,
      _toolBarContainer: null,
      _topMenuContainer: null,
      _windowContent: null,
      _toolBarPosition: "top",
      _toolBarInWindow: false, // define if the toolbar is directly in the window or if it is in the global toolbar
      _disabled: false,
      /**
       * @type {boolean}
       */
      _isCurrent: false,

      /**
       * Store how the window position should be handled
       * @type {string}
       */
      _position: null,

      _positionClass: "gbc_WindowWidget_position_default",
      _processing: false,
      _messageWidget: null,

      /** @type {Boolean} **/
      _isClosable: true,

      /**
       * @type {boolean}
       */
      isModal: false,

      /** @type {boolean}  */
      _forceDefaultSettings: false,

      /**
       * @constructs
       * @param {Object} opts - Options passed to the constructor
       * @publicdoc
       */
      constructor: function (opts) {
        $super.constructor.call(this, opts);
        this._menuContainerTop = this._element.getElementsByClassName("gbc_WindowMenuContainerTop")[0];

        this._menuContainerBottom = this._element.getElementsByClassName("gbc_WindowMenuContainerBottom")[0];
        this._menuContainerLeft = this._element.getElementsByClassName("gbc_WindowMenuContainerLeft")[0];
        this._menuContainerRight = this._element.getElementsByClassName("gbc_WindowMenuContainerRight")[0];
        this._menuContainerMiddle = this._element.getElementsByClassName("gbc_WindowMenuContainerMiddle")[0];

        this._toolBarContainer = this._element.getElementsByClassName("gbc_WindowToolBarContainer")[0];
        this._bottomToolBarContainer = this._element.getElementsByClassName("gbc_WindowBottomToolBarContainer")[0];

        this._topMenuContainer = this._element.getElementsByClassName("gbc_WindowTopMenuContainer")[0];
        this._windowContent = this._element.getElementsByClassName("gbc_WindowContent")[0];

        this._messageWidget = cls.WidgetFactory.createWidget("Message", this.getBuildParameters());
        this._messageWidget.setHidden(true);

        this._position = "default";

        this._topMenus = [];

        this._toolBarWidgets = [];
        this.setClosable(false); //default
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        this._position = null;
        if (context.HostService.getCurrentWindowWidget() === this) {
          context.HostService.unsetCurrentWindowNode();
        }
        const uiWidget = this.getUserInterfaceWidget();
        if (uiWidget?.getActiveWindowWidget() === this) {
          uiWidget.setActiveWindowWidget(null);
        }
        if (this._modalWidget) {
          this.isModal = false;
          this._modalWidget.hide();
          this._modalWidget.destroy();
          this._modalWidget = null;
        }

        this._menuContainerTop = null;
        this._menuContainerBottom = null;
        this._menuContainerLeft = null;
        this._menuContainerRight = null;
        this._menuContainerMiddle = null;
        this._toolBarContainer = null;
        this._topMenuContainer = null;
        this._windowContent = null;
        this._messageWidget.destroy();
        this._messageWidget = null;
        this._topMenus.length = 0;
        this._activeTopMenuWidget = null;
        this._menuWidget = null;
        this._toolBarWidgets = null;

        $super.destroy.call(this);

        gbc.InitService.emit(gbc.constants.widgetEvents.onBeforeUnload); // Store stored settings before leaving
      },

      /**
       * emit window close event
       */
      emitClose: function () {
        if (!this._processing) {
          this.emit(context.constants.widgetEvents.close);
        }
      },

      /**
       * Set Default TTF color
       * @param {string} color - rgb formatted or css name
       */
      setDefaultTTFColor: function (color) {
        context.ChromeActionService.setDefaultTTFColor(color);
      },

      /**
       * set the window title
       * @param {string} text window title
       * @publicdoc
       */
      setText: function (text) {
        this._text = text;
        this._modalWidget?.setHeader(text);
        // The window's <form> root has no visible title inside its own DOM (the visible title lives
        // in the ChromeBar via WindowListService). Mirror it as aria-label so screen readers identify
        // the form landmark by the current window title instead of "unlabeled form".
        this.setAriaAttribute("label", text);
        this.emit(context.constants.widgetEvents.titleChanged, text);
      },

      /**
       * get the window title
       * @publicdoc
       * @returns {string} window title
       */
      getText: function () {
        return this._text;
      },

      /**
       * set the window icon
       * @publicdoc
       * @param {string} image the image to set
       */
      setImage: function (image) {
        this._image = image;
        this._modalWidget?.setImage(image);
        this.emit(context.constants.widgetEvents.iconChanged, image);
      },

      /**
       * get the window icon
       * @publicdoc
       * @return {?string} the icon
       */
      getImage: function () {
        return this._image;
      },

      /**
       * get the window's message widget
       * @publicdoc
       * @return {classes.MessageWidget} the window's message widget
       */
      getMessageWidget: function () {
        return this._messageWidget;
      },

      activate: function () {
        this._isCurrent = true;
        this._modalWidget?.addClasses("gbc_isCurrent");
        context.HostService.setClosableWindowActionActive(this, this.isClosable());
        this.emit(context.constants.widgetEvents.activate);
      },

      disable: function () {
        this._isCurrent = false;
        this._modalWidget?.removeClass("gbc_isCurrent");
        this.emit(context.constants.widgetEvents.disable);
      },

      onActivate: function (hook) {
        return this.when(context.constants.widgetEvents.activate, hook);
      },

      onDisable: function (hook) {
        return this.when(context.constants.widgetEvents.disable, hook);
      },

      /**
       * Return true if window widget is current active window
       * @return {boolean|*}
       */
      isCurrent: function () {
        return this._isCurrent;
      },

      /**
       *
       * @param hidden {boolean} true if the widget is hidden, false otherwise
       */
      setHidden: function (hidden) {
        this._modalWidget?.hide();
        this._activeTopMenuWidget?.setHidden(hidden);
        if (this._toolBarWidgets) {
          for (const toolBar of this._toolBarWidgets) {
            toolBar.setHidden(hidden || this._toolBarPosition === "none");
          }
        }
        this.getLayoutEngine().changeHidden(hidden);
      },

      /**
       * Defines the menu to be displayed as a modal one
       * @param {Object?} opt - options to pass to the new modal
       * @private
       */
      setAsModal: function (opt) {
        if (!this._modalWidget) {
          const buildParameters = this.getBuildParameters();
          buildParameters.storedSettingsKey = opt.storedSettingsKey;
          this._modalWidget = cls.WidgetFactory.createWidget("Modal", buildParameters);
          this._modalWidget.setReverse(this.isReversed());
          this._modalWidget.addClasses("gbc_ModalWindowDialog", "g_needLayout");
          this.getParentWidget().getContainerElement().appendChild(this._modalWidget.getElement());
          this._modalWidget.setParentWidget(this.getParentWidget());
          this._modalWidget.when(gbc.constants.widgetEvents.close, this.emitClose.bind(this));
          this._modalWidget.setClosable(false);
          this._modalWidget.setAuiName(this.getAuiName());
        }
        this._modalWidget.setHeader(this.getText());
        this._modalWidget.setImage(this.getImage());
        this._modalWidget.setContent(this.getElement());
        this._modalWidget.setBackgroundColor(this._backgroundColor);
        this.isModal = true;

        this._modalWidget.show();
        return this._modalWidget;
      },

      /**
       * @inheritDoc
       */
      setAuiName: function (name) {
        $super.setAuiName.call(this, name);
        if (this._modalWidget) {
          this._modalWidget.setAuiName(name);
        }
      },

      /**
       *
       * @return {classes.ModalWidget}
       */
      getModal: function () {
        return this._modalWidget;
      },
      /**
       * Right window menu container
       * @return {Element}
       */
      getWindowMenuContainerRight: function () {
        return this._menuContainerRight;
      },
      /**
       * Main window container which holds menus + forms
       * @return {Element}
       */
      getWindowMiddleContainer: function () {
        return this._menuContainerMiddle;
      },
      /**
       * Main window container which holds forms
       * @return {Element}
       */
      getWindowContentContainer: function () {
        return this._windowContent;
      },
      /**
       *
       * @private
       * @param rtl
       */
      setReverse: function (rtl) {
        $super.setReverse.call(this, rtl);
        this._modalWidget?.setReverse(rtl);
      },

      /**
       * Rule about how the window position should be handled
       * @returns {string} The position rule of the WindowWidget
       */
      getPosition: function () {
        return this._position;
      },

      // TODO GBC-5042 this logic should be into the Position4STBehavior
      /**
       * Set position of the window based on position 4ST attribute.
       * At the moment, only a modal window will be affected by this attribute, a normal window is always displayed full screen.
       * For the modal window, by default it's centered. If 4ST is set to 'field', pos parameter is referent widget.
       * @param {string} pos
       * @param {classes.WidgetBase} invokerWidget
       */
      setPosition: function (pos, invokerWidget) {
        this.removeClass(this._positionClass);
        this._positionClass = `gbc_WindowWidget_position_${pos}`;
        this.addClasses(this._positionClass);
        // setting position to field without having windowType to modal makes no sense,
        // it's considered as modal by GDC but not by HTMLv1.
        if (!invokerWidget || !this._modalWidget) {
          return;
        }
        const appRect = invokerWidget.getApplicationWidget().getElement().getBoundingClientRect(),
          relativeRect = cls.DOMRectHelper.viewportToLocal(appRect, invokerWidget.getElement().getBoundingClientRect());
        const modalLeft = this.isReversed() ? relativeRect.right : relativeRect.left;
        const modalTop = relativeRect.bottom;
        this._modalWidget.setPosition(modalLeft, modalTop);
      },

      /**
       * @inheritDoc
       */
      setNoBorder: function (noBorder) {
        $super.setNoBorder.call(this, noBorder);
        this._modalWidget?.setHeaderHidden(noBorder);
      },

      /**
       * @returns {boolean} true if the widget is hidden, false otherwise
       */
      isVisible: function () {
        return this._element;
      },

      /**
       * @private
       * @param hook
       */
      onClose: function (hook) {
        this.when(gbc.constants.widgetEvents.close, hook);
      },

      /**
       * @private
       * @param closable
       * @param updateCloseAction whether the close action should appear in accordance to the closable param
       * true by default, a case for false could be an override from a 4st attribute to hide the action
       */
      setClosable: function (closable, updateCloseAction = true) {
        this._isClosable = closable;
        this._modalWidget?.setClosable(Boolean(closable));
        if (updateCloseAction) {
          context.HostService.setClosableWindowActionActive(this, closable);
        }
      },

      /**
       * Is window closable?
       * @return {Boolean}
       */
      isClosable: function () {
        return this._isClosable;
      },

      /**
       *
       * @private
       * @param path
       */
      setBackgroundImage: function (path) {
        let prop = null;
        if (path) {
          prop = `url('${path}')`;
        }
        this.setStyle({ "background-image": prop });
      },

      /**
       * @inheritDoc
       */
      setBackgroundColor: function (color) {
        $super.setBackgroundColor.call(this, color);
        this._modalWidget?.setBackgroundColor(color);
      },

      /**
       * @private
       * @param {classes.TopMenuWidget} topMenu
       * @param order
       * @param topMenuContainer
       */
      addTopMenu: function (topMenu, order, topMenuContainer) {
        this.addChildWidget(topMenu, {
          noDOMInsert: true
        });
        if (this._activeTopMenuWidget) {
          this._activeTopMenuWidget.setHidden(true);
        }
        this._topMenus.push(topMenu);
        this._activeTopMenuWidget = topMenu;
        if (topMenuContainer !== this) {
          topMenuContainer.addTopMenu(this._activeTopMenuWidget, order);
        } else {
          this._topMenuContainer.appendChild(this._activeTopMenuWidget.getElement());
        }
        this._activeTopMenuWidget.setHidden(false);
      },

      /**
       *
       * @private
       * @param topMenu
       */
      removeTopMenu: function (topMenu) {
        cls.ArrayHelper.remove(this._topMenus, topMenu);
        if (this._activeTopMenuWidget !== topMenu) {
          return;
        }
        this._activeTopMenuWidget.setHidden(true);
        this._activeTopMenuWidget = this._topMenus.length ? this._topMenus[this._topMenus.length - 1] : null;
        this._activeTopMenuWidget?.setHidden(false);
      },

      /**
       *
       * @private
       * @param container
       * @return {null}
       */
      getMenuContainer: function (container) {
        switch (container) {
          case "top":
            return this._menuContainerTop;
          case "bottom":
            return this._menuContainerBottom;
          case "left":
            return this._menuContainerLeft;
          case "right":
            return this._menuContainerRight;
          default:
            return null;
        }
      },

      /**
       * Add a toolbar to the window
       * @private
       * @param toolBar
       * @param order
       */
      addToolBar: function (toolBar, order) {
        toolBar.setOrder(order);
        this.addChildWidget(toolBar, {
          noDOMInsert: true
        });
        this._toolBarWidgets.push(toolBar);
        cls.DOMElementHelper.prependChild(this._toolBarContainer, toolBar.getElement());
      },

      /**
       *
       * @return {null}
       */
      getMenuContainers: function () {
        return {
          top: this._menuContainerTop,
          bottom: this._menuContainerBottom,
          left: this._menuContainerLeft,
          right: this._menuContainerRight
        };
      },

      /**
       * @param toolBar
       */
      removeToolBar: function (toolBar) {
        this._toolBarWidgets = this._toolBarWidgets.filter((item) => item !== toolBar);
      },

      /**
       * Add a menu to the window
       * @private
       * @param widget
       */
      addMenu: function (widget) {
        this._menuWidget = widget;
        this.addChildWidget(widget, {
          noDOMInsert: true
        });
      },

      /**
       * Get window menu widget
       */
      getMenuWidget: function () {
        return this._menuWidget;
      },

      /**
       * Set the Window Toolbar position
       * @param {String} position - "top", "bottom", "none", or nothing to refresh toolbar positions
       */
      setToolBarPosition: function (position) {
        // when chromebar is hidden, "chrome" position fallbacks to default value "top"
        if (position === "chrome" && !context.MainContainerService.isChromeBarVisible()) {
          position = "top";
        }
        if (position) {
          this._toolBarPosition = position;
          // Mirror the setting to the user interface
          this.getUserInterfaceWidget().setToolBarPosition(position);
        }

        for (const toolBar of this._toolBarWidgets) {
          let effectivePosition = this._toolBarPosition;
          if (toolBar.getPosition() && toolBar.getPosition() !== "default") {
            effectivePosition = toolBar.getPosition();
          }

          switch (effectivePosition) {
            case "top":
              cls.DOMElementHelper.prependChild(this._toolBarContainer, toolBar.getElement());
              toolBar.setHidden(false);
              break;
            case "bottom":
              cls.DOMElementHelper.prependChild(this._bottomToolBarContainer, toolBar.getElement());
              toolBar.setHidden(false);
              break;
            case "none":
              toolBar.setHidden(true);
              break;
          }
        }
      },

      /**
       * Get the Window Toolbar position
       * @return {String} could be "top", "bottom", "none" or "chrome"
       */
      getToolBarPosition: function () {
        return this._toolBarPosition;
      },

      /**
       *
       * @param processing
       * @private
       */
      _setProcessingStyle: function (processing) {
        this._processing = processing;
        this.emit(gbc.constants.widgetEvents.processing, processing);
      },

      /**
       * Ignore stored Settings (for position and size)
       * @param {Boolean} forceDefaultSettings
       */
      setForceDefaultSettings: function (forceDefaultSettings) {
        this._forceDefaultSettings = forceDefaultSettings;
        this._modalWidget?.setForceDefaultSettings(forceDefaultSettings);
      },

      /**
       * @inheritDoc
       */
      addInDom: function () {
        const widget = this._modalWidget || this;
        if (!widget.getElement()) {
          return false;
        }

        widget.removeClass("gbc_out_of_view");
        if (!widget.isElementInDOM()) {
          widget.getParentWidget().getContainerElement().appendChild(widget.getElement());
          return true;
        }
        return false;
      },

      /**
       * @inheritDoc
       */
      removeFromDom: function () {
        const widget = this._modalWidget || this;
        if (!widget.getElement()) {
          return false;
        }

        if (!this.hasChildWebComponent()) {
          widget.getElement().remove();
          return true;
        }
        widget.addClasses("gbc_out_of_view");
        return false;
      }
    };
  });
  cls.WidgetFactory.registerBuilder("Window", cls.WindowWidget);
});
